import { showInfo, showSuccess } from "actions/notification";
import { getRegions } from "actions/region";
import login from "api/authentication";
import {
  confirmUser as confirm,
  deleteUser as destroy,
  getCurrentUser,
  getCurrentUserByToken,
  inviteUser as invite,
  registerUser as register,
  requestPasswordReset as requestPasswordResetApi,
  resetPassword as reset,
  saveUser as save,
  updatePassword as updatePasswordApi,
} from "api/user";
import { push, replace } from "connected-react-router";

import { DataStorage, initializeMoment } from "utils";
import Translate from "utils/translate";
import { getUrlParams } from "utils/urlParams";
import { splitName } from "utils/user";

const { t } = new Translate("notifications");

export const getUser = () => (dispatch) =>
  dispatch({
    type: "USER_GET",
    payload: getCurrentUser(),
  }).then((res) => {
    const { company, locale } = res.value.user;
    initializeMoment(locale);
    DataStorage.initialize(res.value.user);
    dispatch(getRegions(company.countryId));
  });

export const getUserByToken = (email, token, target) => (dispatch) =>
  dispatch({
    type: "USER_GET",
    payload: getCurrentUserByToken(email, token),
  }).then((res) => {
    const { user } = res.value;
    initializeMoment(user.locale);
    DataStorage.initialize(user);
    if (target) dispatch(push(target));
  });

export const loginUser =
  (email, password, companyPage = false) =>
  (dispatch, getState) =>
    dispatch({
      type: "USER_GET",
      payload: login(email, password),
    }).then((res) => {
      const { user } = res.value;
      initializeMoment(user.locale);
      DataStorage.initialize(user);
      if (companyPage && !!getState().portfolioNames.length) {
        dispatch(push(`/assets?portfolioIDs[]=${getState().portfolioNames[0].id}`));
      } else {
        dispatch(push(getUrlParams().redirect || "/"));
      }
    });

export const logoutUser =
  (redirect = true) =>
  (dispatch) => {
    DataStorage.clear();
    if (redirect) dispatch(push("/login"));
    dispatch({ type: "USER_LOGOUT" });
  };

export const inviteUser = (user, assets) => (dispatch) => {
  const assetIDs = assets.map((a) => a.id);

  return dispatch({
    type: "USER_INVITE",
    payload: invite({ ...splitName(user.name), ...user }, assetIDs),
    meta: { assetIDs },
  }).then((res) => {
    const {
      user: { active, fullName },
    } = res.value;
    dispatch(
      showInfo(
        t("user.brokerInvite.success.title", { user: fullName }),
        t(`user.brokerInvite.success.message${active ? "Active" : "New"}`, {
          user: fullName,
        })
      )
    );
  });
};

export const confirmUser = (user) => (dispatch) =>
  dispatch({
    type: "USER_CONFIRM",
    payload: confirm({
      ...user,
      profilePictureAttributes: user.photo && { photo: user.photo },
    }),
  })
    .then((res) => {
      const currentLocale = DataStorage.getUserLocale();
      DataStorage.initialize(res.value.user);
      dispatch(replace(`${window.location.pathname}${window.location.search}`));

      if (currentLocale !== res.value.user.locale) {
        window.location.reload();
      }
    })
    .catch(() => null);

export const requestPasswordReset = (email) => (dispatch) =>
  dispatch({
    type: "USER_PASSWORD_RESET_REQUEST",
    payload: requestPasswordResetApi(email),
  }).then(() =>
    dispatch(
      showInfo(
        t("user.requestPasswordReset.success.title"),
        t("user.requestPasswordReset.success.message")
      )
    )
  );

export const resetPassword = (params) => (dispatch) =>
  dispatch({
    type: "USER_PASSWORD_RESET",
    payload: reset(params),
  }).then((res) => {
    DataStorage.initialize(res.value.user);
    dispatch(push("/"));
    dispatch(
      showSuccess(t("user.resetPassword.success.title"), t("user.resetPassword.success.message"))
    );
  });

export const saveUser =
  (user, shouldRefresh = false) =>
  (dispatch) =>
    dispatch({
      type: "USER_UPDATE",
      payload: save(user),
    }).then((res) => {
      DataStorage.initialize(res.value.user);
      if (shouldRefresh) window.location.reload();
    });

export const updatePassword = (user) => (dispatch) =>
  dispatch({
    type: "USER_PASSWORD_UPDATE",
    payload: updatePasswordApi(user).then(() =>
      dispatch(
        showSuccess(t("user.resetPassword.success.title"), t("user.resetPassword.success.message"))
      )
    ),
  });

export const deleteUser = (user) => (dispatch) =>
  dispatch({
    type: "USER_DELETE",
    payload: destroy(user),
  }).then(() => dispatch(logoutUser()));

export const registerUser =
  (user, { plan = "growth", ...rest }) =>
  (dispatch) =>
    dispatch({
      type: "USER_REGISTER",
      payload: register(user, { plan, ...rest }),
    });

export const getSignedUpUser = (email, token) => (dispatch) =>
  dispatch({
    type: "USER_SIGNUP_GET",
    payload: getCurrentUserByToken(email, token),
  }).then((res) => {
    const { user } = res.value;
    DataStorage.setUserEmail(user.email);
    DataStorage.setUserToken(user.authenticationToken);

    return res;
  });
